import {
  invokeIfDefined,
  isNil,
  notNil,
  stringToInt,
  isEmpty,
  notEmpty,
  getIfNotEmpty,
} from '@megaphonenpm/mputils/src/utils.js';
import {log, warn, fatal} from '@megaphonenpm/mputils/src/logfunctions.js';
import {
  div,
  getElementByIdCaseInsensitive,
  setWidthToHeight,
  awaitImgs,
  hide,
  matchSizeAndPosition,
  show,
  setText,
  processDescendents,
  idToAttribute, awaitRender, getStyleSheet, setCSSRule, removeClassByPrefix, setRuleProperty
} from '@megaphonenpm/mputils/src/DOM.js';
import {cloneInstance, isGWDThemeValid} from "./GWDService.js";
import {
  MULTICHOICE_PRODUCT,
  PRODUCTNAME_BLANK, PRODUCTNAME_FACEBOOKTOTV,
  PRODUCTNAME_FIVESTARS,
  PRODUCTNAME_GUESSGAUGE,
  PRODUCTNAME_INSTAGRAMTOTV,
  PRODUCTNAME_LIVEREACTIONS,
  PRODUCTNAME_MOODMETER,
  PRODUCTNAME_POSTER,
  PRODUCTNAME_SCOREBOARD,
  PRODUCTNAME_TWEETTOTV,
  PRODUCTNAME_UGC, PRODUCTNAME_WHATSAPPTOTV, RATING_PRODUCT,
  STATE_MAIN_COUNTDOWN,
  STATE_MAIN_SCOREBOARD,
  STATE_SCENE,
  STATE_SCENE_RESULT
} from '@megaphonenpm/mrtsclient/src/constants.js';
import {
  CHANGE,
  INITED
} from '@megaphonenpm/mputils/src/constants.js';
import {play, deepPlay} from "./Animation.js";
import {MegaScreen} from "./MegaScreen.js";
import {MediaContainer} from "./MediaContainer.js";
import {ScoreboardComponent} from "./components/ScoreboardComponent.js";
import {AnswersMultichoiceComponent} from "./components/AnswersMultichoiceComponent.js";
import {AnswersFivestarsComponent} from "./components/AnswersFivestarsComponent.js";
import {AnswersGuessGaugeComponent} from "./components/AnswersGuessGaugeComponent.js";
import {AnswersMoodMeterComponent} from "./components/AnswersMoodMeterComponent.js";
import {BodyHeadlineComponent} from "./components/BodyHeadlineComponent.js";
import {TextController} from "./components/TextController.js";
import {MastheadSecondaryTextComponent} from "./components/MastheadSecondaryTextComponent.js";
import {LiveReactionsComponent} from "./components/LiveReactionsComponent.js";
import {ScreenComponent} from "./components/ScreenComponent.js";
import {get} from 'lodash-es';
import {STATE_CLOSED} from '@megaphonenpm/mrtsclient/src/constants.js';
import {DoubleBufferedMediaContainer} from './DoubleBufferedMediaContainer.js';
import {TweetComponent} from './components/TweetComponent.js';
import {TotalVotesComponent} from './components/TotalVotesComponent.js';
import {PostMessages} from './PostMessages.js';

export class RunView extends ScreenComponent {
  constructor (themeID) {
    super(themeID);
  }

  validate () {
    super.validate();
    this.valid = isGWDThemeValid(this.themeID);
  }

  init () {
    if (!this.valid) {
      // The GWD theme main <div> failed to load or initialize properly, so skip initializing this RunView.
      this.dispatchEvent(INITED);
      return;
    }

    this.xPosition = 0;
    this.yPosition = 0;
    this.revealTextTimeoutID = -1;
    this.revealTextStartHeadlineAnimationTimeoutID = -1;

    // Create top-level runview <div>, which contains all content for this runview
    this.view = div(idToAttribute(this.themeID), 'top');
    this.view.setAttribute('data-runview-name', this.getThemeSetting('field_name'));
    // TODO: Wherever approrpiate, change all query selectors from IDs to classes
    document.querySelector('#runviews').append(this.view);

    // Indicates whether a given scene's "start" (intro) operations have completed. Used to determine
    // whether the intro needs to be played when a screen joins during the "result" state (ROUND_ENDPAUSE).
    this.sceneStartComplete = false;

    // Clone the "sizers" div, used to determine the content region for dynamic content such as the
    // answers area and the main scoreboard rankings grid. Add sizers to the DOM so they can be
    // measured in context.
    this.sizers = cloneInstance(this.themeID, 'sizers');
    this.view.append(this.sizers);
    hide(this.sizers);

    // Clone the main content element, which includes the backplate, main title (question text), CTA, etc.
    this.main = this.createMain();
    // TODO: Allow multiple "mainTitleContainer" elements per theme, which would allow different
    //   styles to be applied to different products (e.g., for Tweet to TV vs Hot or Not)
    this.mainTitleContainer = this.main.querySelector('#mainTitleContainer');

    // Create the body content element, which will contain dynamically generated content such as
    // the answers for a question or the rankings in a scoreboard.
    this.body = div('bodyContent');
    this.body.classList.add('bodyContent', 'visible_all_states');  // 'visible_all_states' ensures the body will be visible during all states
    this.body.style.position = 'absolute';

    // Create a overlay layer for all absolutely positioned content that should apppear in front of
    // the body and main elements.
    this.overlays = div('overlays');
    this.overlays.classList.add('overlays', 'visible_all_states');
    this.overlays.style.position = 'absolute';

    // The "dynamic body backplate" is an element created here at runtime to provide a background
    // rectangle behind the main body area, EXCLUDING the question media. For example, in a Viewer's Choice
    // question, the dynamicBodyBackplate matches the size of the answers, but NOT the question media.
    // The background color and shadow of the dynamicBodyBackplate are set via Editor's theme settings on
    // a per-theme, per-state, and per-product basis. The dynamicBodyBackplate is named "dynamic" to
    // distinguish it from any "bodyBackplate" element, that might have been manually created inside a GWD theme.
    //
    // TODO: Add support for dynamic background during non-scene states, such as the winner state
    this.dynamicBodyBackplate = div(null, 'dynamicBodyBackplate');
    this.dynamicBodyBackplate.classList.add('state_scene', 'state_scene_result', 'state_end', 'state_winner', 'state_main_scoreboard');

    // Create a "scene media underlay" layer, which displays the current scene media enlarged and
    // behind the standard scene media. The "scene media underlay" is used by some UGC layouts.
    this.sceneMediaUnderlay = div(null, 'sceneMediaUnderlay');
    this.sceneMediaUnderlay.classList.add('state_scene', 'state_scene_result', 'layout_tweettotv_e');

    // The sceneMediaUnderlay div opts out of the default "start" animation that is applied to
    // all descendants of the .main div. The sceneMediaUnderlay div uses the theme's scene media
    // animation instead.
    this.sceneMediaUnderlay.setAttribute('data-no-scene-animations', 'true');

    // Create AnswersMultichoiceComponent instance for scenes with multiple-choice questions
    // such as Pic Pick, Reader's Choice, and Viewer's Choice
    this.answersMultichoice = new AnswersMultichoiceComponent(this.themeID);

    // Create AnswersFivestarComponent for Five Stars scenes
    this.answersFivestars = new AnswersFivestarsComponent(this.themeID);

    // Create AnswersGuessGaugeComponent for Guess Gauge scenes
    this.answersGuessGauge = new AnswersGuessGaugeComponent(this.themeID);

    // Create AnswersGuessGaugeComponent for Guess Gauge scenes
    this.answersMoodMeter = new AnswersMoodMeterComponent(this.themeID);

    // Create LiveReactionsComponent (displays recent user answers)
    this.liveReactions = new LiveReactionsComponent(this.themeID);
    this.liveReactions.setSize(this.getThemeSetting('design.products.livereactions.screen.default.size.width'),
      this.getThemeSetting('design.products.livereactions.screen.default.size.height'));
    this.liveReactions.setOffset(this.getThemeSetting('design.products.livereactions.screen.default.offset.x'),
      this.getThemeSetting('design.products.livereactions.screen.default.offset.y'));

    // Create BodyHeadlineComponent in which to display Bulletin text, social posts,
    // and Teaser URLs
    this.bodyHeadline = new BodyHeadlineComponent(this.themeID,
      ['bulletin','teaser', 'tweettotv', 'facebooktotv', 'instagramtotv', 'whatsapptotv', 'twitchtotv', 'title', 'ugc']);

    // Create a TextController for every main_title_txt instance found in the theme. Each
    // instance will display question text, teaser subtitle, and social user name. Note that some
    // themes have multiple instances of main_title_txt, which allows different states and products
    // to present the main title in a unique style (i.e., to change font, color, size, location).
    this.mainTitleTxtControllers = [];
    let mainTitleTxtElements = this.main.querySelectorAll('.main_title_txt');
    mainTitleTxtElements.forEach(main_title_txt => {
      this.mainTitleTxtControllers.push(new TextController(main_title_txt, main_title_txt));
    });

    // Wrap the .mainTitleContainer's main_title_txt element in a container <div> so that the
    // masthead's secondary txt element can be positioned together with main_title_txt in a single
    // flex container.
    let mastheadMainTitleTxtElement = this.mainTitleContainer.querySelector('#main_title_txt');
    let allMastheadTitlesElement = div('all_masthead_titles', 'all_masthead_titles');
    if (notNil(mastheadMainTitleTxtElement)) {
      mastheadMainTitleTxtElement.insertAdjacentElement('beforebegin', allMastheadTitlesElement);
      allMastheadTitlesElement.append(mastheadMainTitleTxtElement);
    }

    this.ctaTextControllers = [];
    let ctaTxtElements = this.main.querySelectorAll('.cta_txt');
    ctaTxtElements.forEach(cta_txt => {
      this.ctaTextControllers.push(new TextController(cta_txt, cta_txt.parentElement));
    });

    // Create a MastheadSecondaryTextComponent in which to display question text, teaser
    // subtitle, and social user name
    this.mastheadSecondaryTxt = new MastheadSecondaryTextComponent(this.themeID,['tweettotv', 'instagramtotv']);

    // Main headline
    let mainHeadlineTxt = this.main.querySelector('.headline_txt');
    this.mainHeadlineController = new TextController(mainHeadlineTxt, mainHeadlineTxt.parentElement);

    // Create scoreboards
    this.mainScoreboard = new ScoreboardComponent(this.themeID);
    this.mainScoreboard.configure({
        id: 'main_scoreboard',
        visibleStates: [STATE_MAIN_SCOREBOARD, STATE_SCENE, STATE_SCENE_RESULT],
        visibleProducts: [PRODUCTNAME_SCOREBOARD],
        numRows: this.getThemeSetting('design.scoreboards.screen.main_scoreboard.grid.rows.count'),
        numCols: this.getThemeSetting('design.scoreboards.screen.main_scoreboard.grid.columns.count'),
        rowGap: this.getThemeSetting('design.scoreboards.screen.main_scoreboard.grid.gap.row'),
        columnGap: this.getThemeSetting('design.scoreboards.screen.main_scoreboard.grid.gap.column'),
        rankGap: this.getThemeSetting('design.scoreboards.screen.main_scoreboard.rank.gap.horizontal'),
        hideEmptyRows: false
      }
    );

    this.realtimeScoreboard = new ScoreboardComponent(this.themeID);
    this.realtimeScoreboard.configure({
        id: 'realtime_scoreboard',
        visibleStates: [STATE_SCENE, STATE_SCENE_RESULT],
        hiddenProducts: [PRODUCTNAME_SCOREBOARD],
        numRows: this.getThemeSetting('design.scoreboards.screen.realtime_scoreboard.grid.rows.count'),
        numCols: this.getThemeSetting('design.scoreboards.screen.realtime_scoreboard.grid.columns.count'),
        rowGap: this.getThemeSetting('design.scoreboards.screen.realtime_scoreboard.grid.gap.row'),
        columnGap: this.getThemeSetting('design.scoreboards.screen.realtime_scoreboard.grid.column.row'),
        rankGap: this.getThemeSetting('design.scoreboards.screen.realtime_scoreboard.rank.gap.horizontal'),
        hideEmptyRows: true
      }
    );

    this.totalVotes = new TotalVotesComponent(this.themeID)

    // Append content to this view
    // TODO: Refactor all answer-component code to process answer components as a group rather
    //       than individually. I.e., create a this.answerComponents list and polymorphically iterate
    //       over it.
    if (this.answersMultichoice.valid) {
      this.body.append(this.answersMultichoice.view);
    }
    if (this.answersFivestars.valid) {
      this.body.append(this.answersFivestars.view);
    }
    if (this.answersGuessGauge.valid) {
      this.body.append(this.answersGuessGauge.view);
    }
    if (this.answersMoodMeter.valid) {
      this.body.append(this.answersMoodMeter.view);
    }
    if (this.liveReactions.valid) {
      this.main.append(this.liveReactions.view);
    }
    if (this.bodyHeadline.valid) {
      this.body.append(this.bodyHeadline.view);
    }
    if (this.mastheadSecondaryTxt.valid) {
      allMastheadTitlesElement.append(this.mastheadSecondaryTxt.view);
    }
    if (this.totalVotes.valid) {
      this.overlays.append(this.totalVotes.view);
    }

    // Create scene media underlay media container, and mute it (because it's a clone of the actual
    // scene media, which plays the audio when enabled).
    this.createSceneMediaUnderlayMediaContainer();
    this.sceneMediaUnderlayMediaContainer.setMuted(true);
    this.sceneMediaUnderlay.append(this.sceneMediaUnderlayMediaContainer.view);

    // Add scene media underlay to the main masthead so that it can appear behind the title and
    // subtitle, but in front of the main masthead backplate, which allows for designs in which
    // the masthead backplate shows through a semi-transparent scene-media underlay.
    if (notNil(this.mainTitleContainer)) {
      this.mainTitleContainer.insertAdjacentElement('beforebegin', this.sceneMediaUnderlay);
    }

    this.view.append(this.main);
    this.view.append(this.dynamicBodyBackplate);
    this.view.append(this.body);
    this.view.append(this.overlays);

    // Create scene media
    this.sceneMediaContainer = this.createSceneMedia();

    // Create social icon
    this.createSocialIcon();

    // Create TweetComponent for displaying Tweets in the generic Twitter "look and feel"
    this.genericTweet = this.createGenericTweet();
    if (this.genericTweet.valid) {
      this.main.append(this.genericTweet.view);
    }

    // IMPORTANT: In order to apply answer layouts, the RunView must be in the DOM and visible,
    // so create answers now after answersContainer has been appended to this view, but before
    // hiding the top-level container for this view.
    this.answersMultichoice.createAnswers();

    // Adjustments to RunView required to support UGC layouts in all themes
    this.configureUGCLayouts();

    awaitImgs(this.view).then(() => {
      this.syncToState();
      this.dispatchEvent(INITED);
    });
  }

  setAppState (appState) {
    this.appState = appState;
    this.registerForAppStateChanges();
  }

  registerForAppStateChanges () {
    this.appState.on(CHANGE, e => {
      this.syncToState(e.detail);
    });
  }

  /**
   * If this view is the active theme, synchronizes the view to match part or all of the application
   * state. To updated a single part of the state, pass the changed appState property name and value
   * as a generic obect, as in:
   * {state: STATE_SCENE_RESULT}
   *
   * To update the entire state, invoke syncToState() without arguments.
   *
   * @param changedProp
   */
  syncToState (changedProp) {
    if (!this.valid) {
      if (this.active) {
        const themeName = getIfNotEmpty(this, 'theme.name', null);
        fatal('WARNING: CONTENT DISPLAY FAILURE. SPECIFIED THEME IS MISSING OR INVALID.'
          + ` THEME NAME: [${notNil(themeName) ? themeName : 'Unknown'}], THEME ID: [${this.themeID}]`);

        // Inform parent (Director) that the current theme is invalid (so a warning can be displayed)
        if (get(changedProp, 'prop') === 'state') {
          MegaScreen.globals.postMessageManager.send({
            message: PostMessages.THEME_INVALID,
            data: {themeName: themeName,
              themeID: this.themeID,
              stateName: get(changedProp, 'value'),
              sceneID: get(MegaScreen.globals.appState.scene, 'id')}});
        }

        // Note that the theme failure being reported to Director here occurred in the current
        // instance of MegaScreen only, and does not necessarily affect other MegaScreen instances.
        // Themes are loaded at boot-time only, and then never checked again, so it's possible that
        // the theme failure encountered above might have been corrected (e.g., theme files uploaded
        // to Studio), in which case other MegaScreen instances subsequently opened would not have
        // the same theme error.
        //
        // Future Consideration:
        // For Director to fully report accurately on the state of any given theme, on every runstate
        // change, every MegaScreen instance would have to report its current theme validity state
        // to MRTS, and MRTS would have to share that state with Director. Director could then report
        // on whether each and every MegaScreen instance has a valid theme for the current state (for
        // example, if three of seven MegaScreen instances did not have a valid theme for the current
        // state, Director could display: "Theme missing in 3 of 7 On Air locations".
      }
      return;
    }

    if (this.active) {
      // Web Poll scenes: Handle poll OPEN and poll CLOSED states. This property is set when the
      // current web poll scene changes after the scene has already started.
      if (get(changedProp, 'prop') === 'webpollState') {
        if (this.appState.isCurrentSceneWebPoll()) {
          if (this.appState.webpollState === STATE_CLOSED) {
            this.renderSceneResult();
          } else {
            this.revertSceneResult();
          }
        }
      }

      if (get(changedProp, 'prop') === 'votes') {
        // TODO: IMPORTANT: Don't update components that are not visible. For example, if the
        //  scene is a Mood Meter, update answersMoodMeter ONLY. Audit RunView for other cases
        //  where the implementation performs unnecessary tasks for invisible components.
        this.answersMultichoice.updatePercentages();
        this.answersFivestars.updateAverageVote();
        this.answersGuessGauge.updateAverageVote();
        this.answersMoodMeter.updateAverageVote();
        this.totalVotes.updateVotes();
      }
      if (get(changedProp, 'prop') === 'pointsRemaining') {
        this.updatePointsRemaining();
      }
      if (get(changedProp, 'prop') === 'timeRemaining') {
        if (this.appState.state  === STATE_MAIN_COUNTDOWN) {
          this.updateTimeRemaining(false);
        } else {
          this.updateTimeRemaining();
        }
      }
      if (get(changedProp, 'prop') === 'winner') {
        this.updateWinner();
      }
      if (get(changedProp, 'prop') === 'numAnsweredCorrectly'
          || get(changedProp, 'prop') === 'numAnsweredIncorrectly') {
        this.updateNumAnswered();
      }
      if (get(changedProp, 'prop') === 'numUsers') {
        this.updateNumUsers();
      }
      if (get(changedProp, 'prop') === 'recentResponses') {
        this.liveReactions.setRecentResponses(this.appState.recentResponses);
      }
      if (isNil(changedProp)
              // If the state has changed, and the data required to render the state is ready, then
              // render the state. (Data might not be ready if the state is a web-poll scene whose
              // web-poll record has not yet loaded.)
          || (changedProp.prop === 'state' && this.hasDataRequiredToRenderState(changedProp.value))
              // If the Web Poll record for the current scene has changed, re-render the scene
          || changedProp.prop === 'webpollScene') {

        this.updatePosition();

        this.setStateMediaURIs().then(() => {
            this.sceneMediaContainer.render();
            this.sceneMediaUnderlayMediaContainer.render();

            this.dispatchEvent(RunView.STATE_MEDIA_READY,
            {newState: this.appState.state,
                  oldState: changedProp.prop === 'state' ? get(changedProp, 'oldValue') : null});

            // A new state has been entered, or the webpoll for this scene changed.
            // Update the view to reflect the new state or the new webpoll...

            // When the state changes and this RunView is active then the .applicationRoot's classList
            // will include this theme's CSS class (e.g., "current_screen_theme_network", so all CSS
            // styles from settings and the theme will be applied. It's, therefore, now safe to make
            // any CSS adjustments that that rely on the those styles (as reported by getComputedStyle();
            this.configureBodyHeadline();

            // The product name for the MRTS run's current scene (NOT the nested Web Poll scene)
            let effectiveScreenProductName = MegaScreen.globals.appState.effectiveScreenProductName;

            // For Blank and Poster, the RunView has no content, so abort this update. MegaScreen treats
            // Poster identically whether the current scene is a MRTS scene or a nested Web Poll
            // scene. In both cases, the "poster media" is retrieved from the MRTS scene's
            // content.foreground.media.screen property, and is displayed in MegaScreen's foreground layer.
            if (effectiveScreenProductName === PRODUCTNAME_BLANK
              || effectiveScreenProductName === PRODUCTNAME_POSTER) {
              this.view.classList.remove('active');
              return;
            }

            // Indicates that this view is now the active view (because it matches the current theme)
            // The active view will automatically be made visible via CSS.
            this.view.classList.add('active');

            // Two special cases for STATE_SCENE_RESULT...
            if (this.appState.state === STATE_SCENE_RESULT) {
              if (!this.sceneStartComplete) {
                // STATE_SCENE_RESULT Special Case 1:
                // The scene has not yet rendered (likely because MegaScreen connected to MRTS during
                // the scene result), so start state_scene first, then start the state_scene_result.
                this.doStartState(STATE_SCENE, true).then(() => {
                  this.doStartState(STATE_SCENE_RESULT, false);
                });
              } else {
                // STATE_SCENE_RESULT Special Case 2:
                // The scene has already been rendered, so start state_scene_result, but
                // WITHOUT playing the state intro.
                this.doStartState(this.appState.state, false);
              }
            } else {
              // The new state is not state_scene_result, so invoke the generic start-state function,
              // and play the state's intro animation.
              this.doStartState(this.appState.state, true);
            }
          }
        );
      }
    } else {
      // Indicates that this view is NOT the active view (because it does not match the current
      // theme). Inactive views are automatically hidden via CSS.
      if (isNil(changedProp) || changedProp.prop === 'state') {
        this.view.classList.remove('active');
        this.reset();
      }
    }
  }

  /**
   * Checks if the data required by this RunView to render the current state has loaded. In
   * practice, for MRTS non-scene states and for native MRTS scenes, data is always available, so
   * hasDataRequiredToRenderState() returns true. However, for scene states that load data externally,
   * such as web poll scenes or prediction scenes, data might not yet have loaded. If a data
   * dependency for the specified state has not yet loaded, hasDataRequiredToRenderState()
   * returns false.
   *
   * @param state  One of the state-name constants, such as STATE_SCENE.
   *
   * @returns {boolean}
   */
  hasDataRequiredToRenderState (state) {
    let hasRequiredData = false;

    if (state === STATE_SCENE) {
      let currentSceneSrcID = MegaScreen.globals.appState.getCurrentSceneSrcID();
      let effectiveScreenProductName = MegaScreen.globals.appState.effectiveScreenProductName;

      if (isEmpty(currentSceneSrcID)
          || effectiveScreenProductName === PRODUCTNAME_BLANK
          || effectiveScreenProductName === PRODUCTNAME_POSTER) {
        // hasRequiredData is always true in the following cases:
        // 1) No External src
        // The scene object has no external src, so it is a native MRTS scene, therefore all data
        // is available to render the state (because MRTS always delivers the state data before
        // advancing into the state).
        //
        // 2) The screen scene is BLANK
        // No content is rendered during BLANK scenes, so no dependent data needs to have loaded.
        //
        // 2) The screen scene is POSTER
        // RunView instances do not render the media assets in POSTER scenes (asset display is
        // handled by media layers for POSTER scenes), so no dependent data needs to have loaded.
        hasRequiredData = true;
      } else {
        // The scene object has an external src, so it is a web poll or a prediction scene. Check to
        // see whether the required web poll record or prediction record is available in the
        // dataStore. Web poll and prediction records are both located in the same place:
        // dataStore.webpolls (despite the omission of the word "prediction" in the property name).
        let currentScene = MegaScreen.globals.appState.scene;
        let webpollRecordExists = MegaScreen.globals.dataStore.webpolls.has(currentSceneSrcID);

        if (MegaScreen.globals.sceneService.isScreenWebPollScene(currentScene)) {
          // Web Poll scene: If the record exists, the required data is available
          hasRequiredData = webpollRecordExists;
        } else if (MegaScreen.globals.sceneService.isPredictionScene(currentScene)) {
          // The current scene is a Prediction scene, so now check if it is a Scoreboard or not
          if (MegaScreen.globals.appState.isCurrentSceneExternalScoreboard()) {
            // Prediction Scoreboard scene: The data required to render the main content backplate
            // and title is provided by MRTS, so for the purposes of RunView, the required data is
            // available. If the rankings are not yet avaialable, the child ScoreboardComponent will
            // render updates when the rankings arrive.
            hasRequiredData = true;
          } else {
            // Prediction-question scene: If the record exists, the required data is available
            hasRequiredData = webpollRecordExists;
          }
        }
      }
    } else {
      // For all states OTHER than STATE_SCENE, data is loaded from MRTS and is always available.
      hasRequiredData = true;
    }

    return hasRequiredData;
  }

  /**
   * Updates this RunView's current position to reflect the most-specific position setting for the
   * current state, which is one of the following, in order:
   *   1) Scene screen position
   *   2) ThemeSet screen position
   *
   * After updating this RunView's position, updatePosition() makes that position available via
   * this.xPosition and this.yPosition so that all content in this RunView can access the current
   * position.
   */
  updatePosition () {
    // Get settings
    let sceneX;
    let sceneY;
    if (this.appState.state === STATE_SCENE || this.appState.state === STATE_SCENE_RESULT) {
      // The current state is a scene, so check for a position setting at the scene level. That
      // setting overrides the ThemeSet's setting.
      sceneX = get(this.appState.effectiveScene, 'presentation.screen.position.x');
      sceneY = get(this.appState.effectiveScene, 'presentation.screen.position.y');
    }

    let themeXPosition = get(MegaScreen.globals.themeService.getCurrentThemeSet(), 'layout.screen.position.x', 0);
    let themeYPosition = get(MegaScreen.globals.themeService.getCurrentThemeSet(), 'layout.screen.position.y', 0);

    // Update x
    let previousXPosition = this.xPosition;
    if (notEmpty(sceneX)) {
      this.xPosition = sceneX;
    } else {
      this.xPosition = themeXPosition;
    }
    if (this.xPosition !== previousXPosition) {
      // To avoid unnecessary DOM reflow, assign the new position if it changed only.
      this.view.style.left = `${this.xPosition}px`;
    }

    // Update y
    let previousYPosition = this.yPosition;
    if (notEmpty(sceneY)) {
      this.yPosition = sceneY;
    } else {
      this.yPosition = themeYPosition;
    }
    if (this.yPosition !== previousYPosition) {
      this.view.style.top = `${this.yPosition}px`;
    }

    // Update Generic Twitter Tweet to TV's offset
    this.genericTweet.setPositionOffset(-this.xPosition, -this.yPosition);
  }

  /**
   * Assign the media URIs for the media in the current state. Doing so causes that media to buffer.
   * When all media for the current state is ready, resolve a returned Promise so the RunView can
   * render the state.
   *
   * TODO:
   * This implementation (RunViews dispatching STATE_MEDIA_READY) should be replaced in future with
   * a MegaScreen-wide StateRenderer that synchronizes same-frame renderering across the entire
   * application. See ticket #93:
   * https://playmegaphone.unfuddle.com/a#/projects/36870/tickets/by_number/93
   */
  setStateMediaURIs () {
    let mediaURIUpdatePromises = [];
    if (this.appState.state === STATE_SCENE) {
      let scene = this.appState.effectiveScene;
      let productLayout = MegaScreen.globals.extensionService.cascadingGetSceneLayout(scene, MegaScreen.globals.dataStore.playlist);
      let productName = MegaScreen.globals.sceneService.getScreenProductName(scene);

      let questionMediaURI = get(scene, 'content.question.media.screen.uri');
      let socialMediaURI = get(scene, 'content.social.media.screen.uri');
      let socialAvatarURI = get(scene, 'content.social.avatar.screen.uri');
      let sceneMediaURI = isEmpty(questionMediaURI)
        ? (isEmpty(socialMediaURI) ?  socialAvatarURI : socialMediaURI)
        : questionMediaURI;

      mediaURIUpdatePromises.push(this.sceneMediaContainer.setURI(sceneMediaURI));

      if (MegaScreen.globals.layoutService.isDramatic(productLayout)) {
        if (productName === PRODUCTNAME_TWEETTOTV) {
          mediaURIUpdatePromises.push(this.sceneMediaUnderlayMediaContainer.setURI((`${MegaScreen.globals.themeLoader.getThemeBaseURI()}global/socialbg-twitter.png`)));
        } else {
          mediaURIUpdatePromises.push(this.sceneMediaUnderlayMediaContainer.setURI(sceneMediaURI));
        }
      } else {
        this.sceneMediaUnderlayMediaContainer.setURI(null);
      }
    }

    return Promise.all(mediaURIUpdatePromises);
  }

  /**
   * Invokes the startState() function for the given state, then when startState() resolves,
   * invokes the given state's post-start-state function, if any.
   *
   * @param state The name of the state to start.
   * @param playIntro If true, plays the state's intro animation.
   *
   * @returns A Promise that resolves when both startState() AND the state's post-start-state
   *   function have completed.
   */
  doStartState (state, playIntro) {
    return this.startState(state, playIntro).then(() => {
      // TODO: evaluate whether post_start_state_*() is necessary anymore (after the animation refactor
      //   where setTimeout() was replaced by triggering reflow (via offsetHeight)

      // If this state has a custom "post start" function (e.g., post_start_state_scene()),
      // invoke it now (this occurs after the DOM has been updated in response to starting the
      // #main <div>'s intro animation)
      invokeIfDefined(this,`post_start_${state}`);
    });
  }

  /**
   * Returns true if this RunView's theme matches the current scene's theme. Inactive RunViews
   * are automatically hidden, and should ignore changes to the appState.
   */
  get active () {
    if (isNil(this.appState)) {
      // While the application is booting, the appState might not yet have been provided. Until an
      // appState becomes available, this view is considered inactive (and will be hidden).
      return false;
    } else {
      return this.themeID === MegaScreen.globals.themeService.getCurrentScreenThemeID();
    }
  }

  /**
   * Creates the main content container, which includes global elements such as the general
   * content backplate, the title text, and the CTA text.
   */
  createMain () {
    // The theme's "exporter" div contains all theme content, ready for cloning into MegaScreen
    // TODO: Rename cloneInstance() to cloneFromTheme()
    let main = cloneInstance(this.themeID, 'main');
    if (notNil(main)) {
      main.style.position = 'absolute';
      main.classList.add('visible_all_states');  // Ensures the #main <div> will be visible during all states
    }
    return main;
  }

  /**
   * Sets size of the body div to match the appropriate sizing rectangle provided in the
   * theme source file for the current state. This function determines region that will be
   * made available for answers, scoreboards, and other body content.
   */
  sizeBody () {
    let sizer = this.getCurrentBodySizer();

    if (isNil(sizer)) {
      warn(`Theme ${this.theme.name} missing body-content sizer. Cannot configure body for state [${this.appState.state}].`);
    } else {
      matchSizeAndPosition(sizer, this.body);
      matchSizeAndPosition(sizer, this.dynamicBodyBackplate);

      // Scene Media Underlay: Special case for some product layouts (E.g., UGC Product Layout B)
      let productLayout = MegaScreen.globals.extensionService.cascadingGetSceneLayout(this.appState.effectiveScene, MegaScreen.globals.dataStore.playlist);
      if (productLayout === 'b') {
        let underlaySize = this.getThemeSetting('design.products.ugc.screen.media.underlay.size');
        let underlayPosition = this.getThemeSetting('design.products.ugc.screen.media.underlay.position');

        if (notEmpty(underlaySize) || notEmpty(underlayPosition)) {
          this.sceneMediaUnderlay.style.left = `${get(underlayPosition, 'x', 0)}px`;;
          this.sceneMediaUnderlay.style.top = `${get(underlayPosition, 'y', 0)}px`;;
          this.sceneMediaUnderlay.style.width = `${get(underlaySize, 'width', 400)}px`;
          this.sceneMediaUnderlay.style.height = `${get(underlaySize, 'height', 400)}px`;
        } else {
          // This theme's Theme Designer settings do not specify a size or position for the scene
          // media underlay, so match the width and height of the current body sizer.
          matchSizeAndPosition(sizer, this.sceneMediaUnderlay);
          // In fullscreen themes that lack an explicit size for the scene-media underlay, add a small
          // amount of padding for cosmetic purposes. Note that the detection of a "fullscreen" theme
          // is based solely on the theme name, which is fragile, but structured data is not available
          // in the data model, and in practice, all fullscreen themes do, in fact, include the string
          // "fullscreen" in their name.
          if (this.getThemeSetting('field_name').toLowerCase().includes('fullscreen')) {
            let sceneMediaUnderlayStyles = getComputedStyle(this.sceneMediaUnderlay);
            let sceneMediaUnderlayWidth = parseInt(sceneMediaUnderlayStyles.width);
            let sceneMediaUnderlayHeight = parseInt(sceneMediaUnderlayStyles.height);
            let sceneMediaUnderlayDefaultHorizontalPaddingPercentage = .015;
            let sceneMediaUnderlayDefaultVerticalPaddingPercentage = .06;
            let sceneMediaUnderlayPaddingHorizontal = sceneMediaUnderlayWidth * sceneMediaUnderlayDefaultHorizontalPaddingPercentage;
            let sceneMediaUnderlayPaddingVertical = sceneMediaUnderlayHeight * sceneMediaUnderlayDefaultVerticalPaddingPercentage;

            if (!isNaN(sceneMediaUnderlayPaddingHorizontal)) {
              this.sceneMediaUnderlay.style.width = `${sceneMediaUnderlayWidth + sceneMediaUnderlayPaddingHorizontal}px`;
              this.sceneMediaUnderlay.style.left = `${parseInt(sceneMediaUnderlayStyles.left) - sceneMediaUnderlayPaddingHorizontal/2}px`;
            }

            if (!isNaN(sceneMediaUnderlayPaddingVertical)) {
              this.sceneMediaUnderlay.style.height = `${sceneMediaUnderlayHeight + sceneMediaUnderlayPaddingVertical}px`;
              this.sceneMediaUnderlay.style.top = `${parseInt(sceneMediaUnderlayStyles.top) - sceneMediaUnderlayPaddingVertical/2}px`;
            }
          }
        }
      }
    }
  }

  /**
   * Returns the body-sizer element corresponding to the current state or product from this RunView's
   * GWD theme. Body sizer elements are created at theme-design time in Google Web Designer to
   * indicate the position and dimensions of body content for various products and states.
   */
  getCurrentBodySizer () {
    let sizer;

    // Attempt to retrieve state-specific body sizer, if it exists
    switch(this.appState.state) {
      case STATE_MAIN_SCOREBOARD:
        sizer = this.sizers.querySelector('#sizerMainScoreboard');
        break;

      case STATE_SCENE:
      case STATE_SCENE_RESULT:
        let screenProductName = this.appState.effectiveScreenProductName;
        // Quiz sizers apply to MRTS quiz scenes only (because "web quiz" scenes do not have a
        // countdown and do not have a scoreboard).
        if (MegaScreen.globals.sceneService.isQuizScene(this.appState.effectiveScene)
          && !MegaScreen.globals.appState.isCurrentSceneWebPoll()) {
          // If the scene is a quiz, and there's a product-specific sizer for quiz scenes, use that.
          sizer = getElementByIdCaseInsensitive(`sizerQuiz${screenProductName}`, this.sizers);
          // If there's no product-specific sizer for quiz scenes, check for a quiz-specific sizer (for all products)
          sizer = notNil(sizer) ? sizer : getElementByIdCaseInsensitive('sizerQuiz', this.sizers);
        }
        // If there's no quiz-specific sizer, check for a non-quiz, product specific sizer
        sizer = notNil(sizer) ? sizer : getElementByIdCaseInsensitive(`sizer${screenProductName}`, this.sizers);
        break;
    }

    // If no state-specific, quiz-specific, or product-specific sizer is found, use the general body sizer
    sizer = notNil(sizer) ? sizer : getElementByIdCaseInsensitive('sizerBodyContent', this.sizers);

    return sizer;
  }

  /**
   * Adds a CSS class indicating whether the body's aspect ratio is currently vertical or horizontal
   */
  applyBodyAspectRatioClass () {
    removeClassByPrefix(this.body, 'aspectratio_');
    if ((parseInt(getComputedStyle(this.body).width)
        /parseInt(getComputedStyle(this.body).height))
        >= 1) {
      this.body.classList.add('aspectratio_horizontal');
    } else {
      this.body.classList.add('aspectratio_vertical');
    }
  }

  sizeRealtimeScoreboard () {
    let sizer = this.sizers.querySelector('#sizerRealtimeScoreboard');
    if (isNil(sizer)) {
      warn(`Theme ${this.theme.name} missing realtime-scoreboard sizer. Cannot configure realtime-scoreboard.`);
    } else {
      matchSizeAndPosition(sizer, this.realtimeScoreboard.view);
    }
  }

  /**
   * Creates the "scene media" for this RunView, which displays the question media and social image/video.
   */
  createSceneMedia () {
    let sceneMediaContainer = new DoubleBufferedMediaContainer('question');
    // Add CSS classes indicating the states and layouts during which the scene media should be visible.
    // These classes are used by updateVisibility() to determine whether to hide and show elements.
    sceneMediaContainer.view.classList.add('questionMedia', 'state_scene', 'state_scene_result',
      'layout_ugc_a', 'layout_ugc_b', 'layout_ugc_c', 'layout_ugc_d', 'layout_ugc_e', 'layout_ugc_f');

    // Register to be notified when the screen media container changes size
    new ResizeObserver(this.handleSceneMediaResize.bind(this)).observe(sceneMediaContainer.view);

    return sceneMediaContainer;
  }

  /**
   * Any time the browser updates the scene media container's dimensions, force the scene media
   * to be square, unless the current scene is UGC, in which case let the width adjust automatically.
   */
  handleSceneMediaResize () {
    if (notNil(this.appState)) {
      let constrainWidth = true;
      let scene = this.appState.effectiveScene;
      let productName = this.appState.effectiveScreenProductName;
      let productLayout = MegaScreen.globals.extensionService.cascadingGetSceneLayout(scene, MegaScreen.globals.dataStore.playlist);

      // In the following cases, don't force scene media's width to match its height.
      if (productName === PRODUCTNAME_UGC && productLayout !== 'a'
        ||
        // Generic Twitter: Vertical layout with an image
        (productName === PRODUCTNAME_TWEETTOTV
        && productLayout === 'h'
        && notEmpty(get(scene, 'content.social.media.screen.uri')))
      ) {
        constrainWidth = false;
      }

      if (constrainWidth) {
        setWidthToHeight(this.sceneMediaContainer.view);
      }
    }
  }

  /**
   * Creates the media container to insert behind the scene media for this RunView.
   * The scene media underlay displays an image or video behind the scene media, and is used in
   * some UGC layouts.
   */
  createSceneMediaUnderlayMediaContainer () {
    this.sceneMediaUnderlayMediaContainer = new DoubleBufferedMediaContainer('sceneMediaUnderlayMediaContainer');
    this.sceneMediaUnderlayMediaContainer.view.classList.add('sceneMediaUnderlayMediaContainer', 'state_scene', 'state_scene_result');
  }

  /**
   * Creates the "social icon" for this RunView, which displays the scene's social icon, if any
   */
  createSocialIcon () {
    this.socialIconMediaContainer = new MediaContainer('socialIcon');
    this.socialIconMediaContainer.view.classList.add('socialIcon', 'state_scene', 'state_scene_result');

    // Apply social icon styles based on Theme Designer settings
    let halign = this.getThemeSetting('design.default.screen.media.title_icon.align.horizontal');
    let columnGap = this.getThemeSetting('design.default.screen.media.title_icon.gap.horizontal', 0);
    let marginProperty = halign === 'left' ? 'margin-right' : 'margin-left';

    setCSSRule(getStyleSheet(MegaScreen.globals.themeService.getThemeFieldName(this.themeID)), `${MegaScreen.globals.themeService.getCSSPrefixForTheme(this.themeID)} .mainTitleContainer .socialIcon`,
      `height: ${this.getThemeSetting('design.default.screen.media.title_icon.size.width')};
      margin-top: ${this.getThemeSetting('design.default.screen.media.title_icon.margin.top', 0)}px;
      border-radius: ${this.getThemeSetting('design.default.screen.media.title_icon.border.radius', 0)}px;
      ${marginProperty}: ${columnGap}px;
      overflow: hidden;
      `);
  }

  createGenericTweet () {
    let genericTweet = new TweetComponent(this.themeID);
    genericTweet.usernameElement = this.mainTitleContainer.querySelector('#main_title_txt');
    genericTweet.useridElement = this.mastheadSecondaryTxt.view;
    genericTweet.mastheadContainerElement = this.mainTitleContainer;
    genericTweet.postTextContainerElement = this.bodyHeadline.view;
    genericTweet.mediaElement = this.sceneMediaContainer.view;
    genericTweet.socialIconElement = this.socialIconMediaContainer.view;

    return genericTweet;
  }

  /**
   * Creates the content of the main scoreboard within the ScoreboardComponent referenced by this
   * runview's .mainScoreboard property (which is constructed in this.init()). The resulting
   * scoreboard content reflects the current value of either MegaScreen.globals.appState.overallRankings
   * (during the main scoreboard state) or MegaScreen.globals.appState.sceneRankings (during scenes).
   *
   */
  createMainScoreboard () {
    // Assign main scoreboard title text
    this.updateMainScoreboardTitle();

    // Assign main scoreboard "points" column heading
    setText([this.main.querySelector('.main_scoreboard_points_title_txt')],
      this.getLocaleString('main_scoreboard_points_column_heading'));

    // Recreate the scoreboard every time the MAIN_SCOREBOARD state occurs. This brute-force
    // implementation guarantees that the scoreboard will always reflect the latest theme settings
    // (number of rows, column gap, etc).
    this.mainScoreboard.detach();
    this.mainScoreboard.build();
    this.mainScoreboard.playIntro();

    this.body.append(this.mainScoreboard.view);

    // Clear secondary masthead text
    this.mastheadSecondaryTxt.reset();
  }

  configureBodyHeadline () {
    // Define CSS rules to reduce the body headline font size for certain UGC layouts
    let mainHeadlineTxtStyle = getComputedStyle(this.main.querySelector('#headline_txt'));
    let originalHeadlineFontSize = 100;  // Use 100 as a default in case the main headline text has been removed

    if (notNil(mainHeadlineTxtStyle)) {
      let mainHeadlineFontSize = parseInt(mainHeadlineTxtStyle.fontSize);
      originalHeadlineFontSize = isNaN(mainHeadlineFontSize) ? originalHeadlineFontSize : mainHeadlineFontSize;
    }

    let thisThemeStylesheet = getStyleSheet(MegaScreen.globals.themeService.getThemeFieldName(this.themeID));
    setCSSRule(thisThemeStylesheet, `${MegaScreen.globals.themeService.getCSSPrefixForTheme(this.themeID)}.current_screen_product_ugc.current_product_layout_b .bodyHeadlineContainer #headline_txt`, `font-size: ${originalHeadlineFontSize * .45}px`);
    setCSSRule(thisThemeStylesheet, `${MegaScreen.globals.themeService.getCSSPrefixForTheme(this.themeID)}.current_screen_product_ugc.current_product_layout_c .bodyHeadlineContainer #headline_txt`, `font-size: ${originalHeadlineFontSize * .65}px`);
  }

  /**
   * Adjustments required for UGC layouts to work in themes that lack explicit UGC elements.
   */
  configureUGCLayouts () {
    // To make UGC layouts work in all themes without requiring any legacy theme modifications,
    // automatically add visibility classes to elements in this RunView.
    let sceneVisibleElements = this.view.querySelectorAll('#main .state_scene, #bodyContent .state_scene');

    // NOTE: MegaScreen dynamically generates the '#bodyHeadlineContainer' element at runtime by
    // cloning the GWD instance '#main #headlineContainer'. Therefore, themes wishing to manage
    // #bodyHeadlineContainer's visibility for UGC on a per-layout basis should assign the desired
    // ".layout_ugc_*" class to "#main #headlineContainer". For example, to SHOW
    // '#bodyHeadlineContainer' in "layout c" ONLY, in GWD, add the CSS class 'layout_ugc_c' to
    // '#main #headlineContainer'. To HIDE '#bodyHeadlineContainer' in "layout c", ensure at least
    // one other element in GWD has a UGC layout class, and then do not assign any UGC layout
    // classes to '#main #headlineContainer'.
    let defaultUGCLayoutTitleElements = [
      'ctaContainer',
      'mainTitleContainer',
      'bodyHeadlineContainer'
    ];

    this.configureUGCLayout('a', sceneVisibleElements);  // Standard layout
    this.configureUGCLayout('b', sceneVisibleElements);  // Dramatic layout
    this.configureUGCLayout('c', defaultUGCLayoutTitleElements);  // Fullscreen, with titles
    this.configureUGCLayout('e', defaultUGCLayoutTitleElements);  // Fullscreen dramatic, WITH titles

    // Now ensure the sceneMediaUnderlay ("Ken Burns" effect) is visible during all "Dramatic" layouts.
    // This visibility-class assignment must be done here because MegaScreen dynamically generates the
    // "sceneMediaUnderlay", so the visibility cannot be managed manually in GWD.
    this.sceneMediaUnderlay.classList.add('layout_ugc_b', 'layout_ugc_e', 'layout_ugc_f');
  }

  /**
   * Adjust the current RunView for the specified UGC layout
   *
   * @param layoutName The name of the UGC layout (e.g., 'b').
   * @param defaultUGCLayoutElements A list of elements or element class-names/ids that are considered
   *                                 part of the UGC layout.
   */
  configureUGCLayout (layoutName, defaultUGCLayoutElements) {
    // Check if the theme already has author-time elements that have opted into being visible
    // for the specified layout. If so, then use those and don't automatically make any elements
    // visible for the specified layout.
    let themeLayoutElements = this.view.querySelectorAll(`.layout_ugc_${layoutName}`);

    if (isEmpty(themeLayoutElements)) {
      // The current theme does not have any elements configured to be visible for the specified
      // layout, so automatically make the elements in defaultUGCLayoutElements visible.
      defaultUGCLayoutElements.forEach(elementOrElementName => {
        let element;
        if (typeof elementOrElementName === 'string') {
          element = this.view.querySelector(`.${elementOrElementName}`);
          // If there's no element with the given CSS class name, search for one by ID
          element = isNil(element) ? this.view.querySelector(`#${elementOrElementName}`) : element;
        } else {
          element = elementOrElementName;
        }

        if (notNil(element)) {
          // Add a CSS class to indicate that this element should be visible in the specified layout.
          element.classList.add(`layout_ugc_${layoutName}`);
        }
      });
    }
  }

  /**
   * Shows all state-specific content for the current state and hides all other content, then
   * plays the main content <div>'s intro animation. Returns a Promise that the caller of
   * startState() can use to take action after the main #content animation has safely begun and
   * the DOM has been updated.
   *
   * @param stateName The name of the state to start.
   * @param playIntro If true, plays the intro animation for the state. If not, does not play
   *   the state's intro animation. Instead, just updates the body size and visibility of
   *   content for the state. The intro animation is skipped, for example, by STATE_SCENE_RESULT.
   */
  startState (stateName, playIntro) {
    return new Promise((resolve, reject) => {
      try {
        // Determine whether the new state is STATE_SCENE_RESULT with a duration of 0.
        let isZeroDurationSceneResult = false;
        if (stateName === STATE_SCENE_RESULT) {
          let sceneResultDuration = get(MegaScreen.globals.appState, 'scene.timing.result');
          if (sceneResultDuration === 0) {
            isZeroDurationSceneResult = true;
          }
        }

        // Special case for zero-duration scene result (which is used by titling scenes such as
        // Bulletin, Poster, and UGC): skip all state-startup actions.
        if (isZeroDurationSceneResult) {
          // The current state is "scene result", but the duration of the scene result is zero, so
          // it should be skipped entirely. Therefore, do not proceed with any start-state tasks.
          resolve();
        } else {
          // TODO: In this file, update all uses of productID and productName to use the productID
          //    and productName variables defined here (i.e., pass them as arguments to all dependent
          //    functions rather than retrieving them independently in each function.
          let productID = getIfNotEmpty(this.appState.effectiveScene, 'presentation.screen_product.id');
          let productName = this.appState.effectiveScreenProductName;

          // Adjust size of body div to match the theme's sizer for this state
          this.sizeBody();

          // Add a CSS class indicating whether the body's aspect ratio is vertical or horizontal
          this.applyBodyAspectRatioClass();

          // Stop any reveal text timers that might be in progress
          this.resetRevealText();

          if (!this.appState.isSceneState(stateName)) {
            // The new state is not a "scene state", so pause the sceneMediaContainer video.
            // Note that for scene states, video play/pause occurs in syncToState(), which is
            // invoked before startState().
            this.sceneMediaContainer.pause();
          }

          // If this state has a custom start function (e.g., start_state_scene()), invoke it now
          invokeIfDefined(this, `start_${stateName}`);

          // Apply settings that apply to all states at state-start time.
          this.start_state_all(playIntro, productID, productName, stateName);

          // Hides all content (by removing .visible_state CSS class from each element)
          this.resetVisiblity();

          // Immediately show non-animated content that has opted into being visible during this state.
          // Do this here so that non-animated content that appears in multiple scenes (such as the question
          // backplate) does not briefly disappear at the start of the scene.
          this.updateVisibility(stateName);

          if (!playIntro) {
            // Don't play the intro animation, just resolve inform the caller that startState() has completed
            resolve();
          } else {
            // Play the main intro animation for all descendents of this.main EXCEPT those that have the
            // attribute 'data-no-scene-animations' set to 'true'. Setting 'data-no-scene-animations' to
            // 'true' indicates that the node wishes to opt out of scene animations such as 'start'.
            // TODO: As a performance enhancement, attempt to determine whether the label (CSS class) being assigned
            //  resulted in an animation playing. If not, don't play the animation next time.
            //  Example implementation: pre-process the entire theme at initialization time. Add the CSS class
            //  'start' to all descendents of 'main'. If for any node, getComputedStyle(node).animationName is 'none'
            //  both BEFORE and AFTER the addition of the class 'start', then the node does NOT have a 'start'
            //  animation, and it should be excluded from deepPlay(). To exclude the node from deepPlay(), set
            //  its 'data-no-scene-animations' attribute to 'true'.
            deepPlay(this.main, 'start', 'data-no-scene-animations', 'true');

            // Inform the caller that the startState() task has completed
            resolve();
          }
        }
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * Remove the 'visible_state' class from all descendents of this.view. This hides ALL
   * elements that do not have visibility assigned explicitly elsewhere.
   */
  resetVisiblity () {
    processDescendents(this.view, node => {
      node.classList.remove('visible_state');
    }, null, Node.ELEMENT_NODE);
  }

  /**
   * Show elements that have a CSS class corresponding to the current state. For example,
   * if the current state is state_main_scoreboard, then this code will add the CSS class
   * 'visible_state' to elements that HAVE the CSS class state_main_scoreboard. The CSS
   * rule for the class 'visible_state' makes the elements visible.
   *
   * @param state The state based on which content visibility should be determined.
   * @param filter An optional filter indicating which elements should be included in the update.
   *   Must be one of the following values:
   *   'animated': Update animated elements only.
   *   'nonanimated': Update non-animated elements only.
   *
   *    If no filter is passed, the visibility of all elements is updated.
   */
  // TODO: Remove "filter", as it is no longer reqired due to the animation "offsetHeight"-reflow overhaul
  updateVisibility (state, filter) {
    processDescendents(this.view, (node, compareState) => {
      if (filter === 'nonanimated') {
        if (getComputedStyle(node)['animation-name'] !== 'none') {
          // Skip this animated element
          return;
        }
      }

      if (filter === 'animated') {
        if (getComputedStyle(node)['animation-name'] === 'none') {
          // Skip this NON-animated element
          return;
        }
      }

      if (node.classList.contains('scene_type_quiz')) {
        // Special case for 'quiz' elements (elements with the class 'scene_type_quiz:':
        // If an element contains the class 'scene_type_quiz', then it should be treated as a "MRTS
        // quiz element", so by default, don't show the element UNLESS:
        //   1) the current state is a MRTS quiz
        //   2) the current state has a countdown
        //
        // Example elements include "points remaining" and "time remaining".
        //
        // To force an element to be visible even for MRTS quiz states with NO countdown,
        // themes can add the CSS class "visible_nocountdown". Example element:
        // "realtimeScoreboardBackplate"
        //
        // NOTE: Web Poll quizzes do NOT have a synchronized countdown with time-based points and
        // do NOT have a leaderboard, so for web-poll scenes in a MRTS playlist, elements with the
        // CSS class 'scene_type_quiz' are ALWAYS intentionally hidden.
        //
        // TODO: If, in future, the need to show certain elements during "web quiz" scenes arises,
        //  the 'scene_type_quiz' implementation should be revisited to distinguish between
        //  "synchronized quizzes" (MRTS), and "non-synchronized quizzes" (non-MRTS). The
        //  implementation should also consider granular control over element visibility for quizzes
        //  with and without countdowns, and with and without leaderboards. As of June 11, 2024,
        //  however, the course-grained 'scene_type_quiz' implementation meets the current
        //  requirements (i.e., to hide points/countdown/leaderboard in web-quiz scenes but not in
        //  mrts-quiz scenes).
        if (node.classList.contains(compareState)) {
          if (MegaScreen.globals.sceneService.isQuizScene(this.appState.effectiveScene)
              && !MegaScreen.globals.appState.isCurrentSceneWebPoll()) {
            if (node.classList.contains('visible_nocountdown')) {
                node.classList.add('visible_state');
            } else {
              if (compareState === STATE_SCENE
                  && MegaScreen.globals.sceneService.hasCountdown(this.appState.scene)) {
                node.classList.add('visible_state');
              } else if (compareState === STATE_SCENE_RESULT
                && MegaScreen.globals.sceneService.hasResultCountdown(this.appState.scene)) {
                node.classList.add('visible_state');
              }
            }
          }
        }
      } else {
        // Determine visibility for non-quiz nodes...

        if (node.classList.contains(compareState)) {
          let isVisible = false;
          if (compareState !== 'state_scene' && compareState !== 'state_scene_result') {
            // The current state is not a scene, and the node contains the current state, so show the node
            isVisible = true;
          } else {
            // The current state IS a scene, so now check if the node should be visible for the current scene's product and layout...
            let nodeProducts = [];
            let nodeHiddenProducts = [];
            let nodeLayouts = [];

            node.classList.forEach(className => {
              if (className.indexOf('product_') === 0) {
                nodeProducts.push(className.split('product_')[1].toLowerCase());
              }
              if (className.indexOf('hide_product_') === 0) {
                nodeHiddenProducts.push(className.split('hide_product_')[1].toLowerCase());
              }
              if (className.indexOf('layout_') === 0) {
                nodeLayouts.push(className.split('layout_')[1].toLowerCase());
              }
            });

            let thisSceneProductName = this.appState.effectiveScreenProductName;
            let thisSceneProductLayout = MegaScreen.globals.extensionService.cascadingGetSceneLayout(this.appState.effectiveScene, MegaScreen.globals.dataStore.playlist);
            let productMatches = false;
            let layoutMatches = false;

            // Check if the node should be visible for the current scene's product
            if (nodeProducts.length === 0) {
              // The current state is a scene, the node contains the state, AND the node does not contain any "product-specific"
              // class names (such as "product_fivestars"), so if the layout also matches, show the node. This allows nodes to
              // opt in to visibility for ALL products simply by omitting product-specific class names.
              productMatches = true;

              // If, on the other hand, the node has opted OUT of visibility for the current product with a classname such
              // as "hide_product_bulletin", then hide the node.
              nodeHiddenProducts.forEach(nodeHiddenProduct => {
                if (nodeHiddenProduct === MULTICHOICE_PRODUCT && MegaScreen.globals.productService.isMultichoiceProduct(thisSceneProductName)) {
                  // The node opted into being hidden during ALL multichoice-product scenes, and this scene is a
                  // multichoice-product scene, so hide the node.
                  productMatches = false;
                } else if (nodeHiddenProduct === thisSceneProductName) {
                  // Using a CSS class such as "hide_product_bulletin", the node explicitly opted into being hidden for
                  // this scene's product (e.g., "bulletin"), so hide the node.
                  productMatches = false;
                }
              });
            } else {
              // The current state is a scene and the node contains the state. However, the node also contains at least one
              // "product-specific" class name (such as "product_fivestars", "product_multichoice"), so now check the product...
              // If any of the node's "product-specific" class names match the current scene's product, show the node
              nodeProducts.forEach(nodeProduct => {
                if (
                  (nodeProduct === MULTICHOICE_PRODUCT && MegaScreen.globals.productService.isMultichoiceProduct(thisSceneProductName))
                  || (nodeProduct === RATING_PRODUCT && MegaScreen.globals.productService.isRatingProduct(thisSceneProductName))
                  ) {
                  // The node opted into visibility during ALL multichoice-product scenes, and this scene is a
                  // multichoice-product scene, so show the node.
                  productMatches = true;
                } else if (nodeProduct === thisSceneProductName) {
                  // The node explicitly opted into visibility for this scene's product (e.g., "fivestars").
                  productMatches = true;
                }
              });
            }

            // Check if the node should be visible for the current scene's product layout
            if (isEmpty(thisSceneProductLayout)) {
              // This scene has no product layout. If the node also has no product layout, then it
              // should be visible. If a node specifies a layout, but the scene has no layout, then
              // the node should be hidden.
              let nodeHasLayoutsForThisProduct = nodeLayouts.filter(layoutName => layoutName.includes(thisSceneProductName)).length > 0;
              if (!nodeHasLayoutsForThisProduct) {
                layoutMatches = true;
              }
            } else {
              // This scene has a product layout, so check if the node's layout class matches it.
              if (nodeLayouts.length > 0) {
                nodeLayouts.forEach(nodeLayout => {
                  if (nodeLayout === `${thisSceneProductName}_${thisSceneProductLayout}`) {
                    // This node opted into visibility for the current layout.
                    layoutMatches = true;
                  }
                });
              }
            }

            // If the node has opted into being visible for both the current product and the current
            // layout, then show the node.
            if (productMatches && layoutMatches) {
              isVisible = true;
            }
          }
          if (isVisible) {
            node.classList.add('visible_state');
          }
        }
      }
    }, [state], Node.ELEMENT_NODE);
  }

  /**
   * Apply settings that apply to all states at state-start time. For example, the body backplate
   * color, and main title alignment.
   */

  // TODO: Process all cascading theme setting at theme-load time rather than at
  //  state-start time. Consider moving the CSS that is applied at the element
  //  level (e.g., style="") to class selectors (e.g., '.state_end .main_title_txt').
  start_state_all (playIntro, productID, productName, stateName) {
    if (notEmpty(stateName) && stateName.startsWith('state_')) {
      stateName = stateName.substr(6);
    }

    // Body backplate settings
    let dynamicBodyBackplateColor = this.cascadingGetThemeSetting(
      'body.backplate.background_color', productID, productName, stateName);

    if (notNil(dynamicBodyBackplateColor)) {
      this.dynamicBodyBackplate.style.background = dynamicBodyBackplateColor;
    } else {
      this.dynamicBodyBackplate.style.removeProperty('background');
    }
    let dynamicBodyBackplateShadow = this.cascadingGetThemeSetting(
      'body.backplate.shadow', productID, productName, stateName);
    if (notNil(dynamicBodyBackplateShadow)) {
      this.dynamicBodyBackplate.style.boxShadow = dynamicBodyBackplateShadow;
    } else {
      this.dynamicBodyBackplate.style.removeProperty('box-shadow');
    }

    // Main title settings
    let mainTitleHorizontalAlignment = this.cascadingGetThemeSetting(
      'masthead.main_title.align.horizontal', productID, productName, stateName);
    if (notEmpty(this.mainTitleTxtControllers)) {
      this.mainTitleTxtControllers.forEach(mainTitleTextController => {
        let flexAlignments = {
          'left': 'start',
          'center': 'center',
          'right': 'end'
        }
        // For themes that use display:flex for main_title_txt
        mainTitleTextController.setStyle('justifyContent', flexAlignments[mainTitleHorizontalAlignment]);

        // For themes that use display:block for main_title_txt
        mainTitleTextController.setStyle('textAlign', mainTitleHorizontalAlignment);
      });
    }

    // For all non-scene states, hide the social media icon (it might have been displayed during
    // the previous state).
    // TODO: Create a general "reset()" method to reset all content at the beginning of each state,
    //   then move this social-media icon code there.
    if (stateName !== 'scene' && stateName !== 'scene_result') {
      this.socialIconMediaContainer.setURI(null);
    }

    let mainTitleWrap = this.cascadingGetThemeSetting('masthead.main_title.wrap', productID, productName, stateName);
    if (notEmpty(this.mainTitleTxtControllers)) {
      this.mainTitleTxtControllers.forEach(mainTitleTextController => {
        mainTitleTextController.setStyle('whiteSpace', mainTitleWrap ? 'normal' : 'nowrap');
      });
    }

    // Animations

    // Dynamic body backplate intro animation
    if (playIntro) {
      let dynamicBodyBackplateSceneStartAnimation = this.cascadingGetThemeSetting(
        'body.backplate.animation.start.label', productID, productName, stateName);
      if (notNil(dynamicBodyBackplateSceneStartAnimation )) {
        let dynamicBodyBackplateSceneStartDelay = this.cascadingGetThemeSetting(
          'body.backplate.animation.start.delay', productID, productName, stateName);
        play(this.dynamicBodyBackplate, dynamicBodyBackplateSceneStartAnimation, dynamicBodyBackplateSceneStartDelay);
      }
    }
  }

  /**
   * Initializes the INSTRUCTIONS state.
   */
  start_state_instructions () {
    this.updateNumUsers();
    this.updateTimeRemaining();
  }

  /**
   * Initializes the MAIN_COUNTDOWN state.
   */
  start_state_main_countdown () {
    this.updateTimeRemaining(false);
    setText([this.main.querySelector('.main_countdown_title_txt')],
      this.getLocaleString('main_countdown_title'));
  }

  /**
   * Initializes the SCENE state.
   *
   * TODO: Consider breaking this long function into independent views
   */
  start_state_scene () {
    // TODO: add notNil() checks as required througout this entire function
    // Set a flag indicating that the new scene's setup process is now in progress. Some dependent
    // code uses this flag to render content differently during the setup of a new scene vs a scene
    // that is currently in progress. For example, survey bars for a poll do not animate at
    // the beginning of a new scene, but always animate when a scene is in progress.
    this.sceneStartComplete = false;

    // For non-Web Poll scenes, this.currentScene returns the MRTS scene. For Web Poll scenes,
    // this.currentScene returns the nested Web Poll scene.
    let scene = this.appState.effectiveScene;
    log(`START SCENE (RunView [${this.theme.name}]), ${get(MegaScreen.globals.appState, 'scene.id')}`);

    // Product and theme convenience variables
    // (Product layout for this scene resides in the Product Extension named "layout")
    let productLayout = MegaScreen.globals.extensionService.cascadingGetSceneLayout(scene, MegaScreen.globals.dataStore.playlist);
    let productID = get(scene, 'presentation.screen_product.id');
    let productName = MegaScreen.globals.sceneService.getScreenProductName(scene);

    // Render main scoreboard
    this.createMainScoreboard();


    // Apply CSS class to govern main and secondary title-text arrangement (horizontal or vertical).
    // 1) Always use vertical titles for generic social scenes (e.g., a Twitter-branded Tweet to TV).
    // 2) Always use horizontal titles for UGC.
    // 3) Otherwise, use vertical titles unless mainTitleContainer is under 100px tall, in which case,
    // use vertical titles.
    setTimeout(() => {
      if (!this.isGenericSocialStyle(productName, productLayout)
          &&
          (productName === PRODUCTNAME_UGC
           || this.mainTitleContainer.clientHeight < 100)) {
        this.mainTitleContainer.classList.add('masthead_title_orientation_horizontal');
        this.mainTitleContainer.classList.remove('masthead_title_orientation_vertical');
      } else {
        this.mainTitleContainer.classList.remove('masthead_title_orientation_horizontal');
        this.mainTitleContainer.classList.add('masthead_title_orientation_vertical');
      }
    }, 0);

    if (this.mainTitleContainer.previousSibling !== this.sceneMediaUnderlay) {
      this.sceneMediaUnderlay.insertAdjacentElement('afterend', this.mainTitleContainer);
    }

    // If this scene has scene media, add it to the DOM now (location varies by theme), then
    // configure scene-media appearance, and play scene-media start animation
    if (notNil(this.sceneMediaContainer.uri)) {
      let sceneMediaParent = this.cascadingGetThemeSetting(
        'media.question.parent', productID, productName,'scene', 'body');

      if (productName === PRODUCTNAME_UGC
          || productName === PRODUCTNAME_INSTAGRAMTOTV) {
        // Temporary special hard-coded case for UGC, Instagram, and Tweet to TV with Tweet media:
        // Always place social image/video in the theme bodyContent area. In future, Theme Designer
        // should be upgraded to allow per-product placement of the social image/video.
        sceneMediaParent = 'body';
      } else if (productName === PRODUCTNAME_TWEETTOTV) {
        if (notEmpty(get(scene, 'content.social.media.screen.uri'))) {
          // This scene has a social image or video
          if (isNil(productLayout)) {
            sceneMediaParent = 'body';
          } else {
            // Tweet to TV with a layout means the scene should be rendered in Twitter's style,
            // so put the social image in the body headline container.
            sceneMediaParent = 'bodyHeadlineContainer';
          }
        } else {
          // This scene does NOT have a social image or video, so the sceneMedia is the user avatar
          if (notNil(productLayout)) {
            // Tweet to TV with a layout means the scene should be rendered in Twitter's style,
            // so put the user avatar in the main masthead.
            sceneMediaParent = 'main';
          }
        }
      }

      // Add scene media container to DOM, at the location specified by either product layout or the theme
      let sceneMediaContainerParent = this.body;
      if (productName === PRODUCTNAME_UGC && MegaScreen.globals.layoutService.isFullscreen(productLayout)) {
        if (MegaScreen.globals.layoutService.hasTitles(productLayout)) {
          // For UGC fullscreen layouts with titles ('c' and 'e'), place the scene media immediately
          // behind the main title
          if (notNil(this.mainTitleContainer)) {
            this.mainTitleContainer.insertAdjacentElement('beforebegin', this.sceneMediaContainer.view);
          }
        } else {
          // For UGC fullscreen layouts without titles ('d' and 'f'), place the scene media after the body
          this.view.insertAdjacentElement('beforeend', this.sceneMediaContainer.view);
        }
      } else {
        // No DOM location was specified by the product layout, so use the theme-specified location, if any
        if (sceneMediaParent === 'bodyHeadlineContainer') {
          sceneMediaContainerParent = this.view.querySelector('#bodyHeadlineContainer')
        } else if (sceneMediaParent === 'main') {
          if (notNil(this.mainTitleContainer)) {
            sceneMediaContainerParent = this.mainTitleContainer;
          } else {
            warn(`No mainTitleContainer found in theme ${this.theme.name}. Could not display scene media.`);
          }
        }
        if (this.sceneMediaContainer.view.parentNode !== sceneMediaContainerParent) {
          sceneMediaContainerParent.insertAdjacentElement('afterbegin', this.sceneMediaContainer.view);
        }
      }

      // Scene media alignment
      let column_gap = this.cascadingGetThemeSetting(
        'media.question.gap.horizontal', productID, productName,'scene', 10);

      // Reset scene-media styles to default
      sceneMediaContainerParent.classList.remove('flexFlowReverse');
      this.sceneMediaContainer.view.style.removeProperty('position');
      this.sceneMediaContainer.view.style.removeProperty('left');
      this.sceneMediaContainer.view.style.removeProperty('right');
      this.sceneMediaContainer.view.style.removeProperty('margin');
      this.sceneMediaContainer.view.style.removeProperty('margin-right');
      this.sceneMediaContainer.view.style.removeProperty('margin-left');
      this.sceneMediaContainer.view.style.removeProperty('margin-top');
      this.sceneMediaContainer.view.style.removeProperty('margin-bottom');
      this.sceneMediaContainer.view.style.removeProperty('border');
      this.sceneMediaContainer.view.style.removeProperty('border-radius');
      this.sceneMediaContainer.view.style.removeProperty('overflow');
      this.sceneMediaContainer.view.style.removeProperty('box-shadow');
      this.sceneMediaContainer.view.style.removeProperty('width');
      this.sceneMediaContainer.view.style.removeProperty('height');
      this.sceneMediaContainer.view.style.removeProperty('animation');

      // Reset scene-media underlay styles to default
      this.sceneMediaUnderlay.style.removeProperty('margin-left');
      this.sceneMediaUnderlay.style.removeProperty('margin-top');
      this.sceneMediaUnderlay.style.removeProperty('animation');

      // Apply cascading 'loop' setting to the scene media (affects videos only)
      let loopSettingPath = MegaScreen.globals.productService.isSocialProduct(productName) ? 'social' : 'question';
      let loop = MegaScreen.globals.cascadingSettingsService.getSettingIfNotEmpty(`content.${loopSettingPath}.media.screen.loop`, scene, MegaScreen.globals.dataStore);
      loop = isEmpty(loop) ? true : loop;  // If no loop setting is specified, default to true
      this.sceneMediaContainer.setLoop(loop);
      this.sceneMediaUnderlayMediaContainer.setLoop(loop);

      // Adjust the scene media's size and location as specified by the current product layout
      let displaySceneMediaFullscreen = false;
      let displaySceneMediaUnderlayFullscreen = false;
      if (MegaScreen.globals.layoutService.isFullscreen(productLayout)) {
        if (productName === PRODUCTNAME_UGC) {
          displaySceneMediaFullscreen = true;
          displaySceneMediaUnderlayFullscreen = true;
        } else if (productName === PRODUCTNAME_TWEETTOTV) {
          displaySceneMediaUnderlayFullscreen = true;
        }
      }

      if (displaySceneMediaUnderlayFullscreen) {
        // Size/position the scene media underlay (backdrop for "Ken Burns" layouts)
        this.sceneMediaUnderlay.style.width = '1920px';
        this.sceneMediaUnderlay.style.height = '1080px';
        this.sceneMediaUnderlay.style.marginLeft = `-${this.xPosition}px`;
        this.sceneMediaUnderlay.style.marginTop = `-${this.yPosition}px`;
        this.sceneMediaUnderlay.style.animation = 'none';
        this.sceneMediaUnderlay.style.top = '0';
        this.sceneMediaUnderlay.style.left = '0';
      }

      if (displaySceneMediaFullscreen) {
        // Size/position the scene media for fullscreen UGC layouts
        this.sceneMediaContainer.view.style.width = '1920px';
        this.sceneMediaContainer.view.style.height = '1080px';
        this.sceneMediaContainer.view.style.marginLeft = `-${this.xPosition}px`;
        this.sceneMediaContainer.view.style.marginTop = `-${this.yPosition}px`;
        this.sceneMediaContainer.view.style.animation = 'none';
      } else {
        // Appy scene-media alignment styles
        let sceneMediaHAlign = this.cascadingGetThemeSetting(
          'media.question.align.horizontal', productID, productName, 'scene');

        let isVerticalArrangementWithSceneMediaInBody = sceneMediaParent === 'body' && this.body.classList.contains('aspectratio_vertical');

        if (sceneMediaHAlign === 'right') {
          sceneMediaContainerParent.classList.add('flexFlowReverse');
          // Add requested space between scene media and neighbouring text (e.g., question or social message)
          if (isVerticalArrangementWithSceneMediaInBody) {
            // Social products are arranged vertically when the theme's aspect ratio is vertical, so
            // treat "left" column gap as "top" margin.
            this.sceneMediaContainer.view.style.marginTop = `${column_gap}px`;
          } else {
            // Note special case: If the current scene is a UGC scene with the non-fullscreen
            // "Ken Burns" layout, then don't apply question-media gap.
            if (productLayout !== 'b') {
              this.sceneMediaContainer.view.style.marginLeft = `${column_gap}px`;
            }
          }
        } else if (sceneMediaHAlign === 'center') {
          this.sceneMediaContainer.view.style.position = 'absolute';
          this.sceneMediaContainer.view.style.left = '0';
          this.sceneMediaContainer.view.style.right = '0';
          this.sceneMediaContainer.view.style.margin = '0 auto';
        } else {
          // Add requested space between scene media and neighbouring text (e.g., question or social message)
          if (isVerticalArrangementWithSceneMediaInBody) {
            // Social products are arranged vertically when the theme's aspect ratio is vertical, so
            // treat "right" column gap as "bottom" margin.
            this.sceneMediaContainer.view.style.marginBottom = `${column_gap}px`;
          } else {
            // Note special cases:
            //   Don't apply question-media gap WHEN:
            //    -The current scene is a UGC scene with the non-fullscreen "Ken Burns" layout
            //    -The current scene is a Tweet to TV with a Generic Tweet layout
            if (productLayout !== 'b'
                && !this.isGenericSocialStyle(productName, productLayout)) {
              this.sceneMediaContainer.view.style.marginRight = `${column_gap}px`;
            }
          }
        }

        // Apply scene-media border
        let sceneMediaBorderStroke = this.cascadingGetThemeSetting(
          'media.question.border.stroke', productID, productName, 'scene');
        if (notNil(sceneMediaBorderStroke)) {
          this.sceneMediaContainer.view.style.border = sceneMediaBorderStroke;
        }
        let sceneMediaBorderRadius = this.cascadingGetThemeSetting(
          'media.question.border.radius', productID, productName, 'scene');
        if (notNil(sceneMediaBorderRadius)) {
          this.sceneMediaContainer.view.style.borderRadius = `${sceneMediaBorderRadius}px`;
          this.sceneMediaContainer.view.style.overflow = 'hidden';
        }

        // Apply scene-media shadow
        let sceneMediaShadow = this.cascadingGetThemeSetting(
          'media.question.shadow', productID, productName, 'scene');
        if (notNil(sceneMediaShadow)) {
          this.sceneMediaContainer.view.style.boxShadow = sceneMediaShadow;
        }

        // Adjust scene media size by amount specified by theme
        let resize = this.cascadingGetThemeSetting(
          'media.question.resize', productID, productName, 'scene');
        if (notNil(resize)
            && !this.isGenericSocialStyle(productName, productLayout)) {
          // The current theme has a resize value for question media, and the current scene is NOT
          // a generic social scene (such as Tweet to TV in the Twitter brand), so apply the
          // resize amount to question media.
          this.sceneMediaContainer.view.style.height = `calc(100% + ${resize}px`;
        } else {
          // Remove previous scene's size setting, if any
          this.sceneMediaContainer.view.style.removeProperty('height');
        }

        // Wait for the browser to update all dimensions, then force the scene media to be square
        // (unless the current scene is UGC, in which case let the width adjust automatically).
        awaitRender().then(() => {
          this.handleSceneMediaResize();
        });
      }

      // Play the scene media animation, delayed by the amount specified by the theme
      // TODO: In the spec, rename these settings from "question." to "scene." because they apply to all uses of
      //   scene media (for example, including social images and videos).
      let sceneMediaAnimationLabel = this.cascadingGetThemeSetting(
        'media.question.animation.start.label', productID, productName,'scene');
      let sceneMediaAnimationDelay = this.cascadingGetThemeSetting(
        'media.question.animation.start.delay', productID, productName,'scene');
      play(this.sceneMediaContainer.view,
        sceneMediaAnimationLabel,
        sceneMediaAnimationDelay);

      play(this.sceneMediaUnderlay,
        sceneMediaAnimationLabel,
        sceneMediaAnimationDelay)
    }

    // Style the dynamic body backplate (the <div> behind the bodyContent area, which contains answers,
    // social message, headline, etc)
    let sceneAnswers = get(scene, 'content.answers');

    // Social icon
    show(this.socialIconMediaContainer.view);
    if (productID === '55ccfbfb6430610003060000') {
      this.socialIconMediaContainer.setURI(`${MegaScreen.globals.themeLoader.getThemeBaseURI()}global/socialicon-twitter.png`);
    } else if (productID === '55cd4ff96464640003120000') {
      this.socialIconMediaContainer.setURI(`${MegaScreen.globals.themeLoader.getThemeBaseURI()}global/socialicon-instagram.png`);
    } else if (productID === '55cd50516464640003130000') {
      this.socialIconMediaContainer.setURI(`${MegaScreen.globals.themeLoader.getThemeBaseURI()}global/socialicon-facebook.png`);
    } else if (productID === '5db88a5edd50c50004000012') {
      this.socialIconMediaContainer.setURI(`${MegaScreen.globals.themeLoader.getThemeBaseURI()}global/socialicon-whatsapp.png`);
    } else if (productID === '5f35bfc8d84ec8000311f030') {
      this.socialIconMediaContainer.setURI(`${MegaScreen.globals.themeLoader.getThemeBaseURI()}global/socialicon-twitch.png`);
    } else if (productName === PRODUCTNAME_UGC) {
      this.socialIconMediaContainer.setURI(`${MegaScreen.globals.themeLoader.getThemeBaseURI()}global/socialicon-ugc.png`);
    } else {
      this.socialIconMediaContainer.setURI(null);
      hide(this.socialIconMediaContainer.view);
    }
    if (notNil(this.mainTitleContainer)) {
      if (this.getThemeSetting('design.default.screen.media.title_icon.align.horizontal') === 'right'
          || this.isGenericSocialStyle(productName, productLayout)) {
        this.mainTitleContainer.insertAdjacentElement('beforeend', this.socialIconMediaContainer.view);
      } else {
        this.mainTitleContainer.insertAdjacentElement('afterbegin', this.socialIconMediaContainer.view);
      }
    } else {
      warn(`No mainTitleContainer found in theme ${this.theme.name}. Could not append social media icon.`);
    }
    setWidthToHeight(this.socialIconMediaContainer.view);

    // Clear secondary masthead text (social userid)
    this.mastheadSecondaryTxt.reset();

    // Body section (question answers, headline)
    if (MegaScreen.globals.productService.isMultichoiceProduct(productName)) {
      this.answersMultichoice.setAnswersJSON(sceneAnswers);
      this.answersMultichoice.reset();
      this.answersMultichoice.playIntro();
    }

    if (productName === PRODUCTNAME_FIVESTARS) {
      this.answersFivestars.reset();
      this.answersFivestars.playIntro();
    }

    if (productName === PRODUCTNAME_GUESSGAUGE) {
      this.answersGuessGauge.reset();
      this.answersGuessGauge.setAnswerText();
      this.answersGuessGauge.playIntro();
    }

    if (productName === PRODUCTNAME_MOODMETER) {
      this.answersMoodMeter.reset();
      this.answersMoodMeter.setAnswerText();
      this.answersMoodMeter.playIntro();
    }

    if (productName === PRODUCTNAME_LIVEREACTIONS) {
      this.liveReactions.reset();
      this.liveReactions.playIntro();
    }

    this.bodyHeadline.playIntro();

    if (this.totalVotes.valid) {
      this.totalVotes.reset();
      this.totalVotes.playIntro();
    }

    // Assign scene text
    let questionText = get(scene, 'content.question.text');
    let titleText = get(scene, 'content.title.text');
    let subtitleText = get(scene, 'content.subtitle.text');
    let socialNameText = get(scene, 'content.social.display_name.text');
    let socialMessageText = get(scene, 'content.social.message.text');
    let sociaUseridText = get(scene, 'content.social.user_id.text');

    // The main masthead text is either the question text, if available, or the subtitle text
    // Teaser uses .title for the masthead. Bulletin does not have a masthead.
    let mastheadMainText = '';
    if (notNil(questionText) && questionText !== '') {
      mastheadMainText = questionText;
    } else if (notNil(titleText) && titleText !== '') {
      // Special case for Prediction scenes: Remove the text "Leaderboard for", which is inserted
      // into the scene by the Prediction CMS when the scene is added to a MRTS playlist.
      if (MegaScreen.globals.sceneService.isPredictionScene(scene)
        && MegaScreen.globals.sceneService.getProductName(scene).toLowerCase() === PRODUCTNAME_SCOREBOARD) {
        titleText = titleText.replace('Leaderboard for ', '');
      }
      mastheadMainText = titleText;
    } else if (notNil(socialNameText) && socialNameText !== '') {
      mastheadMainText = socialNameText;
    }

    // For certain layouts, automatically adjust the default position of the mainTitleContainer.
    // Themes can override these styles with custom CSS, or by creating a new "mainTitleContainer"
    // element that specifically opts into the current layout.
    if (productName === PRODUCTNAME_UGC
        && MegaScreen.globals.layoutService.isFullscreen(productLayout)
        && MegaScreen.globals.layoutService.hasTitles(productLayout)) {
      // UGC Fullscreen Layouts with Titles ('c' and 'e'): Position mainTitleContainer at the top left
      setRuleProperty('main',
       '.current_screen_product_ugc.layout_isfullscreen.layout_hastitles #mainTitleContainer',
        'left',
        `-${this.xPosition}px`);
      setRuleProperty('main',
       '.current_screen_product_ugc.layout_isfullscreen.layout_hastitles #mainTitleContainer',
        'top',
        `-${this.yPosition}px`);
    }

    // Determine the body headline text based on the current scene's product
    let headlineText = '';
    if (productName === 'title') {
      // For the Title product, the body headline text is the .subtitle text
      headlineText = subtitleText;
      // For social products, the body headline text is the social message
    } else if (MegaScreen.globals.productService.isSocialProduct(productName)) {
      if (notEmpty(socialMessageText)) {
        headlineText = socialMessageText;
      }
    } else {
      // For all other products...
      // Assign the body headline based on the following order of precedence (highest to lowest):
      // 1) Subtitle
      // 2) Title
      //
      // Rationale for assigning Subtitle first:
      // Teaser has both a .title and .subtitle, so it uses .subtitle for the body headline.
      // Bulletin has .title text only, so it uses .title for the headline.
      if (notNil(subtitleText) && subtitleText !== '') {
        headlineText = subtitleText;
      } else if (notNil(titleText) && titleText !== '') {
        headlineText = titleText;
      }
    }

    // Use notNil() rather than notEmpty() so that users can intentionally leave the
    // headline empty by supplying the empty string.
    if (notNil(headlineText)) {
      // Reset body styling and DOM location that might have been set by prior UGC scenes.
      this.body.append(this.bodyHeadline.view);

      // In some UGC layouts, adjust headline_txt's styles
      if (productName === PRODUCTNAME_UGC) {
        if (MegaScreen.globals.layoutService.isFullscreen(productLayout)
            && MegaScreen.globals.layoutService.hasTitles(productLayout)) {
          this.body.insertAdjacentElement('afterend', this.bodyHeadline.view);
          let bodyHeadlineWidth = 810;
          let bodyHeadlineHeight = 200;

          setRuleProperty('main',
            '.current_screen_product_ugc.layout_isfullscreen.layout_hastitles .bodyHeadlineContainer',
            'width',
            `${bodyHeadlineWidth}px`);

          setRuleProperty('main',
            '.current_screen_product_ugc.layout_isfullscreen.layout_hastitles .bodyHeadlineContainer',
            'height',
            `${bodyHeadlineHeight}px`);

          setRuleProperty('main',
            '.current_screen_product_ugc.layout_isfullscreen.layout_hastitles .bodyHeadlineContainer',
            'left',
            `${960 - (bodyHeadlineWidth/2) - this.xPosition}px`);

          setRuleProperty('main',
            '.current_screen_product_ugc.layout_isfullscreen.layout_hastitles .bodyHeadlineContainer',
            'top',
            `${1080 - bodyHeadlineHeight - this.yPosition}px`);
        }
      }

      this.bodyHeadline.setText(headlineText);
    }

    if (notEmpty(this.mainTitleTxtControllers)) {
      this.mainTitleTxtControllers.forEach(mainTitleTextController => {
        mainTitleTextController.setText(mastheadMainText);
      });
    }

    // Masthead Secondary Text (the right-aligned text in most lower third themes)
    // 1) If the scene has a social user id, use that as the secondary text.
    // 2) Otherwise, if the scene has subtitleText AND subtitleText is different from headlineText,
    // then use the subtitleText as the secondary text. (Rationale: If the headlineText and the
    // subtitleText are the same, then the body headline will already show the headlineText, so
    // showing that same content again in the secondary text would be redundant. This case happens
    // with the Teaser product, where the subtitleText is used as the body headline.)
    if (notEmpty(sociaUseridText)) {
      this.mastheadSecondaryTxt.setText(sociaUseridText);
    } else if (notEmpty(subtitleText) && (subtitleText !== headlineText)) {
      this.mastheadSecondaryTxt.setText(subtitleText);
    }

    // Assign CTA text
    this.updateCTA(productName, productLayout);

    // Assign "scene count" text
    setText([this.main.querySelector('.scene_count_name_txt')],
      this.getLocaleString('scene_count_name'));
    let sceneCount;

    // REGARDING THE USE OF this.appState.scene IN THE FOLLOWING SECTION OF CODE:
    // In order to retrieve the scene count for the scene in the MRTS playlist, use this.appState.scene,
    // which refers to the MRTS scene. Do NOT use the local 'scene' variable or this.currentScene,
    // both of which, for Web Poll scenes, would refer to the nested Web Poll scene.
    if (MegaScreen.globals.sceneService.isQuizScene(scene)) {
      sceneCount = MegaScreen.globals.playlistService.getQuizSceneIndex(MegaScreen.globals.dataStore.playlist, this.appState.scene.id)+1;
    } else {
      sceneCount = MegaScreen.globals.playlistService.getSceneIndex(this.appState.scene);
    }
    setText([this.main.querySelector('.scene_count_txt')],
      sceneCount);

    // Assign "scene of" text
    setText([this.main.querySelector('.scene_count_of_txt')],
      this.getLocaleString('scene_count_of'));

    // Assign "num scenes" text
    let numScenes;
    if (MegaScreen.globals.sceneService.isQuizScene(scene)) {
      numScenes = MegaScreen.globals.playlistService.getQuizScenes(MegaScreen.globals.dataStore.playlist).length;
    } else {
      numScenes = get(MegaScreen.globals.dataStore, 'playlist.scenes.length')
    }
    setText([this.main.querySelector('.num_scenes_txt')],
      numScenes);

    // Assign "num answered" text
    setText([this.main.querySelector('.num_answered_title_txt')],
      this.getLocaleString('num_answered_title'));
    this.updateNumAnswered();

    // Assign "num users" text
    setText([this.main.querySelector('.num_users_title_txt')],
      this.getLocaleString('num_users_title'));
    this.updateNumUsers();

    // Assign "realtime scoreboard" text
    setText([this.main.querySelector('.realtime_scoreboard_title_txt')],
      this.getLocaleString('realtime_scoreboard_title'));

    // Update realtime scoreboard
    if (this.realtimeScoreboard.valid) {
      this.realtimeScoreboard.detach();
      this.realtimeScoreboard.build(this.appState.sceneRankings);
      // Show realtime scoreboard during quiz scenes only
      this.realtimeScoreboard.view.classList.add('scene_type_quiz');
      this.sizeRealtimeScoreboard();
      this.view.append(this.realtimeScoreboard.view);
      this.realtimeScoreboard.playIntro();
    }

    // Generic Tweet to TV
    // If the scene's product is Tweet to TV AND the scene specifies a layout, then render the scene
    // using Twitter's generic design rather than in the theme's design.
    if (this.isGenericSocialStyle(productName, productLayout)) {
      this.genericTweet.setScene(scene);
      // Reset the TweetComponent's position to 0,0 (offset the ThemeSet's position so the
      // TweetComponent can position itself on the 1080p canvas without being influenced by the
      // ThemeSet's position settings.)
      this.genericTweet.setPositionOffset(-this.xPosition, -this.yPosition);
      this.bodyHeadline.setText(headlineText);
    } else {
      this.genericTweet.setScene(null);
    }

    // Product extensions
    let extensions = get(scene, 'mixins');
    if (notEmpty(extensions)) {
      extensions.forEach(extension => {
        let extensionText = get(extension, 'text', null);
        if (notNil(extensionText)) {
          let extensionElements = this.view.querySelectorAll(`.extension_${extension.field_name}`);
          extensionElements.forEach(extensionElement => {
            extensionElement.textContent = extensionText;
          });
          if (extension.field_name !== 'layout' && extensionElements.length === 0) {
            warn(`Theme [${this.theme.name}] is missing extension element(s) for extension`
              + ` [${extension.field_name}], with value: [${extensionText}].`
              + ` To ensure [${extension.field_name}] is rendered, provide one or more elements`
              + ` in the theme with the CSS class name: [extension_${extension.field_name}].`);
          }
        }
      });
    }

    this.sceneStartComplete = true;
  }

  /**
   * Performs post-start tasks for the SCENE state.
   */
  post_start_state_scene () {
    // Now that 'play' has been issued on all content, perform adjustments that can
    // be made only after the DOM has updated. These adjustments are applied immediately
    // AFTER startScene() makes the scene content visible, but before the browser
    // updates the screen.

    // Update percentages. Do this after startState() completes to allow the survey bar
    // percentages the opportunity to reset to zero without any animation. The call to
    // updatePercentages() here will trigger an animation (up from zero, if votes are
    // greater than zero). See resetPercentages() and disableSurveyBarAnimations().
    this.answersMultichoice.updatePercentages();
    this.answersFivestars.updateAverageVote();
    this.answersGuessGauge.updateAverageVote();
    this.answersMoodMeter.updateAverageVote();
    this.totalVotes.updateVotes();

    if (MegaScreen.globals.sceneService.isQuizScene(this.appState.effectiveScene)) {
      this.updatePointsRemaining();
    }

    this.updateTimeRemaining();

    // If the current scene is a web poll scene, check if the poll is closed. If it is, render
    // the poll-closed (scene result) presentation. (Web Poll scenes don't have a MRTS SCENE_RESULT
    // state. They are, themselves, either "OPEN" or "CLOSED".)
    if (MegaScreen.globals.appState.isCurrentSceneWebPoll()) {
      if (this.appState.webpollState === STATE_CLOSED) {
        this.renderSceneResult();
      }
    }
  }

  /**
   * Initializes the MAIN_SCOREBOARD state.
   */
  start_state_main_scoreboard () {
    this.createMainScoreboard();
  }

  /**
   * Initializes the run END state.
   */
  start_state_end () {
    // Assign run end title text
    this.updateEndTitle();

    // Assign run end headline
    this.updateEndHeadline();

    // Clear secondary masthead text
    this.mastheadSecondaryTxt.reset();
  }

  /**
   * Initializes the WINNER state.
   */
  start_state_winner () {
    // Assign winner title text
    this.updateWinnerTitle();

    // Assign winner text
    this.updateWinner();

    // Clear secondary masthead text
    this.mastheadSecondaryTxt.reset();
  }

  updateWinnerTitle () {
    let titleText = this.getLocaleString('winner_title');

    // Populate all specific end-title text fields
    setText(this.main.querySelectorAll('.winner_title_txt'),
      titleText);

    // Populate all generic main-title text fields
    this.mainTitleTxtControllers.forEach(mainTitleTxtController => {
      mainTitleTxtController.setText(titleText);
    });
  }

  updateWinner () {
    let winnerText =  get(this.appState, 'winner.label');

    // Populate all specific end-title text fields
    setText(this.main.querySelectorAll('.winner_username_txt'),
      winnerText);

    // Populate all generic main-title text fields
    this.mainHeadlineController.setText(winnerText);
  }

  updatePointsRemaining () {
    let pointsSuffix = this.getThemeSetting('design.countdowns.scene.screen.default.points.suffix.visible') ?
      this.getLocaleString('points_suffix') : '';
    let pointsRemaining = this.appState.pointsRemaining === '' ? '' : `${this.appState.pointsRemaining}${pointsSuffix}`;
    setText([this.main.querySelector('.points_remaining_txt')], pointsRemaining);
  }

  updateTimeRemaining (includePrefix = true) {
    let timeRemaining = parseInt(this.appState.timeRemaining);
    let timeRemainingString = '';
    if (parseInt(timeRemaining) < 100000) {
      if (includePrefix) {
        if (timeRemaining < 10 && timeRemaining > -1) {
          // When time remaining is less than 10, add a leading zero
          timeRemainingString = `:0${timeRemaining}`;
        } else {
          timeRemainingString = `:${timeRemaining.toString()}`;
        }
      } else {
        timeRemainingString = timeRemaining.toString();
      }
    }

    setText(this.main.querySelectorAll('.time_remaining_txt'), timeRemainingString);
    this.main.querySelectorAll('.time_remaining_txt').forEach(node => {
      // For the sake of performance, don't play the animation unless the node opts into it
      // in the theme using the "visible state" system (see updateVisibility()).
      if (node.parentElement.classList.contains('timeRemainingContainer')
          && node.parentElement.classList.contains('visible_state')) {
        play(node.parentElement, 'start');
      }
    })
  }

  updateNumAnswered () {
    let numAnswered = stringToInt(this.appState.numAnsweredCorrectly) + stringToInt(this.appState.numAnsweredIncorrectly);
    setText([this.main.querySelector('.num_answered_txt')], numAnswered);
  }

  updateNumUsers () {
    setText(this.main.querySelectorAll('.num_users_txt'),
      stringToInt(this.appState.numUsers));
  }

  updateMainScoreboardTitle () {
    let titleText = this.getLocaleString('main_scoreboard_title');

    // Populate all specific scoreboard-title text fields
    setText(this.main.querySelectorAll('.main_scoreboard_title_txt'),
      titleText);

    // Populate all generic main-title text fields
    this.mainTitleTxtControllers.forEach(mainTitleTxtController => {
      mainTitleTxtController.setText(titleText);
    });
  }

  updateEndTitle () {
    let titleText = this.getLocaleString('run_end_title');

    // Populate all specific end-title text fields
    setText(this.main.querySelectorAll('.end_title_txt'),
      titleText);

    // Populate all generic main-title text fields
    this.mainTitleTxtControllers.forEach(mainTitleTxtController => {
      mainTitleTxtController.setText(titleText);
    });
  }

  updateEndHeadline () {
    let headlineText = this.getLocaleString('run_end_headline');

    // Populate all end-state-specific headline text fields contained within the  #main div
    setText(this.main.querySelectorAll('.end_headline_txt'),
      headlineText);

    // Populate generic main-title text field (which is contained within the #main div)
    this.mainHeadlineController.setText(headlineText);
  }

  updateCTA (currentSceneProductName, currentSceneProductLayout) {
    // Retrieve scene CTA (always retrieve from the MRTS scene, which is accessed here via this.appState.scene).
    let sceneCTAText = getIfNotEmpty(this.appState.scene, 'content.cta.text');
    let sceneCTAType = getIfNotEmpty(this.appState.scene, 'content.cta.type');

    // Retrieve playlist CTA
    let playlistCTAText = getIfNotEmpty(MegaScreen.globals.dataStore.playlist, 'defaults.content.cta.text');
    let playlistCTAType = getIfNotEmpty(MegaScreen.globals.dataStore.playlist, 'defaults.content.cta.type');

    // Determine CTA text by cascading from scene, to playlist, to project settings
    let ctaSettings = [
      {text: sceneCTAText, type: sceneCTAType},
      {text: playlistCTAText, type: playlistCTAType}
    ];

    let ctaText;
    for (let i = 0; i < ctaSettings.length; i++) {
      let ctaSetting = ctaSettings[i];
      if (notNil(ctaSetting.text)) {
        ctaText = ctaSetting.text;
      } else if (notNil(ctaSetting.type)) {
        ctaText = get(MegaScreen.globals.dataStore.deploymentSettings,`defaults.content.cta.strings[${ctaSetting.type}].text`);
      }
      if (notNil(ctaText)) {
        // Found a CTA, so stop cascading
        break;
      }
    }
    if (isNil(ctaText)) {
      // Neither scene nor playlist specify a CTA, so use the project setting
      ctaText = get(MegaScreen.globals.dataStore.deploymentSettings, 'defaults.content.cta.strings.general.text');
    }

    // Assign CTA text to visual components
    if (notEmpty(this.ctaTextControllers)) {
      this.ctaTextControllers.forEach(ctaTextController => {
        ctaTextController.setText(ctaText);
      });
    }

    // For built-in UGC layouts, adjust CTA styles
    if (currentSceneProductName === PRODUCTNAME_UGC
        && (MegaScreen.globals.layoutService.isFullscreen(currentSceneProductLayout) && MegaScreen.globals.layoutService.hasTitles(currentSceneProductLayout))) {
      let ctaContainer = this.view.querySelector('.ctaContainer');

      if (notNil(ctaContainer)) {
        let newLeft = 0 - this.xPosition + 50;
        let newTop = 1080 - this.yPosition - ctaContainer.clientHeight - 25;

        let thisThemeStylesheet = getStyleSheet(MegaScreen.globals.themeService.getThemeFieldName(this.themeID));
        setCSSRule(thisThemeStylesheet, `${MegaScreen.globals.themeService.getCSSPrefixForTheme(this.themeID)}.current_screen_product_ugc.layout_isfullscreen.layout_hastitles .ctaContainer`,
        `left: ${newLeft}px; top: ${newTop}px; text-shadow: #000 1px 0 60px; color: #fff;`);
        setCSSRule(thisThemeStylesheet, `${MegaScreen.globals.themeService.getCSSPrefixForTheme(this.themeID)}.current_screen_product_ugc.layout_isfullscreen.layout_hastitles .cta_txt`,
        `text-align: left;`);
      }
    }
  }

  resetRevealText () {
    clearTimeout(this.revealTextTimeoutID);
    clearTimeout(this.revealTextStartHeadlineAnimationTimeoutID);

    // Reset reveal text (for quiz scenes)
    let answersContainers = this.view.querySelectorAll('.answersContainer');
    if (notEmpty(answersContainers)) {
      answersContainers.forEach(answersContainer => {
        answersContainer.classList.remove('fade-out');
      });
    }
  }

  /**
   * Initializes the SCENE_RESULT state.
   */
  start_state_scene_result () {
    if (!this.appState.isCurrentSceneWebPoll()) {
      this.renderSceneResult();
    }
  }

  renderSceneResult () {
    this.answersMultichoice.startSceneResult();
    this.answersFivestars.startSceneResult();
    this.answersGuessGauge.startSceneResult();
    this.answersMoodMeter.startSceneResult();

    let scene = this.appState.effectiveScene;
    if (MegaScreen.globals.sceneService.isQuizScene(scene)) {
      let revealText = get(scene, 'content.reveal.text');
      if (notEmpty(revealText)) {
        // For quiz questions, display "reveal text", if there is any for this scene
        this.revealTextTimeoutID = setTimeout(() => {
          let answersContainers = this.view.querySelectorAll('.answersContainer');
          if (notEmpty(answersContainers)) {
            answersContainers.forEach(answersContainer => {
              answersContainer.classList.add('fade-out');
            });

            this.revealTextStartHeadlineAnimationTimeoutID = setTimeout(() => {
              answersContainers.forEach(answersContainer => {
                answersContainer.classList.remove('visible_state');
              });
              this.bodyHeadline.view.classList.add('visible_state');
              this.bodyHeadline.setText(revealText);
              this.bodyHeadline.playIntro();
            }, 500);
          }
        }, 5000);
      }
    }
  }

  /**
   * Removes scene-result presentation styles, and returns the current scene to its non-result
   * state. The revertSceneResult() method is used when a web poll changes from CLOSED to OPEN
   * after a scene has already started.
   */
  revertSceneResult () {
    this.answersMultichoice.resetSceneResultStyles();
    this.answersFivestars.revertSceneResult();
    this.answersGuessGauge.revertSceneResult();
    this.answersMoodMeter.revertSceneResult();
  }

  /**
   * Clears all content and deactivates the theme's runview.
   */
  reset () {
    this.sceneMediaContainer.pause();
    this.sceneMediaUnderlayMediaContainer.pause();
  }

  /**
   * Returns true if the specified productName is a social product (e.g., Tweet to TV) AND the
   * specified productLayout is not empty. If a social product such as Tweet to TV has no layout
   * for the current scene, then isGenericSocialStyle() returns false, indicating that the scene
   * should be rendered in this runview's standard theme.
   */
  isGenericSocialStyle (productName, productLayout) {
    let productsThatSupportGenericSocialStyles = [PRODUCTNAME_TWEETTOTV,
      PRODUCTNAME_INSTAGRAMTOTV,
      PRODUCTNAME_FACEBOOKTOTV,
      PRODUCTNAME_WHATSAPPTOTV];
    return productsThatSupportGenericSocialStyles.includes(productName) && notEmpty(productLayout);
  }
}

RunView.STATE_MEDIA_READY = 'STATE_MEDIA_READY';
